import PropTypes from 'prop-types';

/**
 * Core
 */

export const translation = PropTypes.string;
export const translations = PropTypes.oneOfType([
    PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)),
    PropTypes.objectOf(PropTypes.string),
]);

export const routes = PropTypes.objectOf(PropTypes.string);

export const intl = PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
});

export const message = PropTypes.shape({
    defaultMessage: PropTypes.string,
    description: PropTypes.string,
});

export const text = PropTypes.oneOfType([message, PropTypes.string]);

export const label = PropTypes.oneOfType([message, PropTypes.node]);

export const statusCode = PropTypes.oneOf([404, 401, 403, 500]);

export const ref = PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
        current: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    }),
]);

export const components = PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
);

/**
 * Forms
 */
export const errors = PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]);
export const formErrors = PropTypes.objectOf(errors);

export const selectOption = PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
});
export const selectOptions = PropTypes.arrayOf(selectOption);

export const formField = PropTypes.shape({
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    name: PropTypes.string,
});
export const formFields = PropTypes.objectOf(formField);

/**
 * Auth
 */
export const user = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
});

/**
 * Menu
 */
export const menuItem = PropTypes.shape({
    id: PropTypes.string,
    label,
    url: PropTypes.string,
    external: PropTypes.bool,
    active: PropTypes.bool,
});

export const menuItems = PropTypes.arrayOf(menuItem);
export const menus = PropTypes.objectOf(menuItems);

/**
 * Resources
 */
export const date = PropTypes.string;

export const localized = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string),
]);

export const imageSize = PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
});
export const imageSizes = PropTypes.arrayOf(imageSize);

export const image = PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    sizes: imageSizes,
});
export const images = PropTypes.arrayOf(image);

export const video = PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    duration: PropTypes.number,
});
export const videos = PropTypes.arrayOf(video);

const metadataShape = {
    title: PropTypes.string,
    description: PropTypes.string,
    image,
    // keywords: PropTypes.arrayOf(PropTypes.string),
    // favicon: PropTypes.string,
};

export const pageMetadata = PropTypes.shape(metadataShape);

export const page = PropTypes.shape({
    id: PropTypes.string.isRequired,
    url: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    metadata: pageMetadata,
});
export const pages = PropTypes.arrayOf(page);

export const block = PropTypes.shape({
    id: PropTypes.string.isRequired,
});

export const blocks = PropTypes.arrayOf(block);

export const embed = PropTypes.shape({
    // @TODO
    url: PropTypes.string,
    iframeUrl: PropTypes.string,
    type: PropTypes.oneOf(['video']),
    provider: PropTypes.oneOf(['youtube', 'vimeo']),
});

export const notification = PropTypes.shape({
    text: PropTypes.string.isRequired,
    image,
});

export const arrowDirection = PropTypes.oneOf(['right', 'left', 'top', 'bottom']);

export const notifications = PropTypes.objectOf(notification);

// TODO
export const author = PropTypes.shape({ name: PropTypes.string, image, url: PropTypes.string });

export const article = PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.oneOf(['article', 'news', 'embed', 'newsletter']),
    title: PropTypes.string,
    description: PropTypes.string,
    author,
    tags: PropTypes.arrayOf(PropTypes.string),
    datePublished: PropTypes.string,
    blocks,
});

export const articles = PropTypes.arrayOf(article);

/* Projects */
// export const project = PropTypes.shape({
//     title: PropTypes.string,
//     subtitle: PropTypes.string,
//     href: PropTypes.string,
//     background: PropTypes.shape({
//         src: PropTypes.string,
//     }),
//     logo: PropTypes.shape({
//         align: PropTypes.oneOf(['left', 'right']),
//         src: PropTypes.string,
//     }),
//     tags: PropTypes.arrayOf(
//         PropTypes.shape({
//             id: PropTypes.string,
//             label: PropTypes.string,
//             href: PropTypes.string,
//         }),
//     ),
//     theme: PropTypes.shape({
//         colors: PropTypes.shape({
//             primary: PropTypes.string,
//             secondary: PropTypes.string,
//         }),
//     }),
// });

export const id = PropTypes.string;
export const url = PropTypes.string;
export const country = PropTypes.shape({
    id,
    name: localized,
});
export const broadcaster = PropTypes.shape({
    // Indiquer si les droits sont disponibles?
    id,
    name: localized, // ou PropTypes.string
    image,
    url,
    country: PropTypes.string,
});
export const broadcasters = PropTypes.arrayOf(broadcaster);
export const person = PropTypes.shape({
    id,
    name: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
});
export const people = PropTypes.arrayOf(person);
export const credit = PropTypes.shape({
    title: PropTypes.string,
    people,
});
export const credits = PropTypes.arrayOf(credit);
export const tag = PropTypes.shape({
    id,
    label: localized,
});
export const tags = PropTypes.arrayOf(tag);
export const market = PropTypes.shape({
    id,
    label: localized,
});
export const markets = PropTypes.arrayOf(market);
export const colors = PropTypes.shape({
    primary: PropTypes.string,
    secondary: PropTypes.string,
});
export const theme = PropTypes.shape({
    colors,
    backgroundText: PropTypes.string,
});

export const awardInstitution = PropTypes.shape({
    id,
    name: localized,
    image,
});
export const award = PropTypes.shape({
    institution: awardInstitution,
    category: localized,
    nominated: PropTypes.bool,
    year: PropTypes.string,
});
export const awards = PropTypes.arrayOf(award);

export const format = PropTypes.shape({
    type: PropTypes.string,
    year: PropTypes.string,
});
export const formats = PropTypes.arrayOf(format);

export const client = PropTypes.shape({
    name: PropTypes.string,
    image,
    url: PropTypes.string,
});
export const clients = PropTypes.arrayOf(client);

export const tvSeason = PropTypes.shape({
    year: PropTypes.number,
    episodesCount: PropTypes.number,
    episodeMinutes: PropTypes.number,
    unfinished: PropTypes.bool,
});
export const tvSeasons = PropTypes.arrayOf(tvSeason);

export const projectFormat = PropTypes.shape({
    type: PropTypes.oneOf(['season', 'documentary']),
});
export const projectFormats = PropTypes.arrayOf(projectFormat);

export const projectMetadata = PropTypes.shape({
    ...metadataShape,
    video,
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            url,
        }),
    ),
});

export const project = PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string,
    summary: PropTypes.string,
    image,
    logo: image,
    theme,
    blocks,
    url,
    credits,
    tags,
    markets,
    awards,
    metadata: projectMetadata,
});

export const projectTv = PropTypes.shape({
    ...project,
    episodeCount: PropTypes.number,
    episodeDuration: PropTypes.number,
    mainBroadcasters: broadcasters,
    otherBroadcasters: broadcasters,
    formats: projectFormats,
});

export const address = PropTypes.shape({
    name: PropTypes.string,
    street: PropTypes.string,
    city: PropTypes.string,
    zipCode: PropTypes.string,
    country: PropTypes.string,
});

export const contactLink = PropTypes.shape({
    label: PropTypes.string,
    type: PropTypes.string,
    courriel: PropTypes.string,
});

export const cta = PropTypes.shape({
    external: PropTypes.bool,
    label: PropTypes.string,
    url: PropTypes.string,
    type: PropTypes.string,
});

export const contact = PropTypes.shape({
    title: PropTypes.string,
    backgroundColor: PropTypes.string,
    backgroundImage: image,
    address,
    description: PropTypes.string,
    links: PropTypes.arrayOf(contactLink),
    cta,
});

export const card = PropTypes.shape({
    type: PropTypes.oneOf(['image', 'custom', 'year']),
    handle: PropTypes.string,
    image,
    description: PropTypes.string,
});

export const cards = PropTypes.arrayOf(card);

export const site = PropTypes.shape({
    id: PropTypes.string,
    handle: PropTypes.string,
});

export const teamMember = PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    image: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ url: PropTypes.string })]),
});

export const teamMembers = PropTypes.arrayOf(teamMember);

/**
 * Employee Guide
 */

export const pill = PropTypes.shape({
    type: PropTypes.string,
    text: PropTypes.string,
    color: PropTypes.string,
    width: PropTypes.number,
    src: PropTypes.string,
});

export const pills = PropTypes.arrayOf(pill);

export const titlePill = PropTypes.shape({
    video: PropTypes.string,
    title: PropTypes.string,
    text: PropTypes.string,
    color: PropTypes.string,
});

export const titlePills = PropTypes.arrayOf(titlePill);

export const employeeCard = PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    text2: PropTypes.string,
});

export const timelineModal = PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    image: PropTypes.string,
    layout: PropTypes.oneOf(['imageTop', 'imageRight', 'imageLeft', 'columns']),
});

export const timelineModals = PropTypes.arrayOf(timelineModal);

export const timelineDetail = PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    image: PropTypes.string,
    video: PropTypes.string,
    modals: timelineModals,
});

export const timelineDetails = PropTypes.arrayOf(timelineDetail);
